import { BattlePreview } from "../models/BattlePreview";

const TOKEN_KEY = "wwwai_token";
const REFRESH_KEY = "wwwai_refresh";
const USER_KEY = "wwwai_user";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY)) ?? null;
  } catch {
    return null;
  }
}

export function storeSession(access_token, refresh_token, user) {
  localStorage.setItem(TOKEN_KEY, access_token);
  localStorage.setItem(REFRESH_KEY, refresh_token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

export function updateStoredUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

// Refresh tokens and return new access_token. Exported so AuthContext's
// session-restore flow can reuse this instead of re-implementing the same
// fetch + localStorage writes itself.
export async function doRefresh() {
  const refresh_token = getRefreshToken();
  if (!refresh_token) throw new Error("No refresh token");
  const res = await fetch("/api/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token }),
  });
  if (!res.ok) throw new Error("Refresh failed");
  const data = await res.json();
  // Update tokens in storage (keep existing user object)
  localStorage.setItem(TOKEN_KEY, data.access_token);
  localStorage.setItem(REFRESH_KEY, data.refresh_token);
  return data.access_token;
}

// Authenticated fetch — adds Bearer token, handles 401 with one refresh + retry.
export async function authFetch(url, options = {}) {
  const token = getToken();
  const headers = { ...(options.headers ?? {}), "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res = await fetch(url, { ...options, headers });

  // Only worth attempting a refresh if there's a refresh token to use — a
  // signed-in user can legitimately hit this with a stale/missing access
  // token but a valid refresh token (the normal expiry case). A caller with
  // no refresh token either was never signed in (e.g. an anonymous request
  // hitting an auth-required endpoint) and has nothing to refresh; retrying
  // anyway would surface a misleading "session expired" message for someone
  // who was never signed in in the first place.
  if (res.status === 401 && getRefreshToken()) {
    try {
      const newToken = await doRefresh();
      headers["Authorization"] = `Bearer ${newToken}`;
      res = await fetch(url, { ...options, headers });
    } catch {
      clearSession();
      window.dispatchEvent(new Event("auth:signedout"));
      throw new Error("Session expired. Please sign in again.");
    }
  }

  return res;
}

export async function apiSignUp(username, email, password, acceptedPolicies = []) {
  const res = await fetch("/api/auth/signup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, email, password, acceptedPolicies }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Sign up failed.");
  return data; // { pending: true, message, email }
}

// Generic policy-acceptance endpoint — first (and today, only) consumer is
// the Rules-update modal, but not Rules-specific (see the backend's
// config/policyRegistry.js). `documentTypes` is a list of policy types the
// user is acknowledging, never a version — the server always resolves and
// persists the current registry version itself.
export async function apiAcceptPolicies(documentTypes, source) {
  const res = await authFetch("/api/account/policy-acceptances", {
    method: "POST",
    body: JSON.stringify({ documentTypes, source }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error ?? "Could not save your acknowledgment.");
    err.errorCode = data.errorCode;
    throw err;
  }
  return data; // { accepted: [{ documentType, documentVersion, acceptedAt }] }
}

export async function apiSignIn(email, password) {
  const res = await fetch("/api/auth/signin", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Sign in failed.");
  storeSession(data.access_token, data.refresh_token, data.user);
  return data; // { access_token, refresh_token, user: { id, email, username } }
}

export async function apiSignOut() {
  const token = getToken();
  try {
    await fetch("/api/auth/signout", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } finally {
    clearSession();
  }
}

export async function apiGetMe() {
  const res = await authFetch("/api/auth/me");
  if (!res.ok) return null;
  return res.json(); // { user, profile, access }
}

export async function apiCheckUsername(username) {
  const res = await fetch(`/api/auth/check-username?username=${encodeURIComponent(username)}`);
  if (!res.ok) return { available: false };
  return res.json(); // { available: boolean }
}

export async function apiGetHistory({ offset = 0, limit = 10, signal } = {}) {
  const res = await authFetch(`/api/battle/history?limit=${limit}&offset=${offset}`, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json(); // { items: [{ id, char1, char2, winner_name, searched_at }], hasMore }
  return { items: BattlePreview.fromList(data.items), hasMore: data.hasMore };
}

export async function apiResendConfirmation(email) {
  const res = await fetch("/api/auth/resend-confirmation", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || "Request failed.");
    err.errorCode = data?.errorCode;
    throw err;
  }
  return data;
}

export async function apiForgotPassword(email) {
  const res = await fetch("/api/auth/forgot-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error("Request failed.");
  return res.json();
}

export async function apiExchangeCode(code) {
  const res = await fetch("/api/auth/exchange-code", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Invalid or expired reset link.");
  return data; // { access_token }
}

export async function apiResetPassword(access_token, password) {
  const res = await fetch("/api/auth/reset-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ access_token, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to reset password.");
  return data;
}

export async function apiUpdateProfile(username) {
  const res = await authFetch("/api/account/profile", {
    method: "PATCH",
    body: JSON.stringify({ username }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Could not update username.");
  return data.profile;
}

export async function apiChangePassword(currentPassword, newPassword) {
  const res = await authFetch("/api/account/change-password", {
    method: "POST",
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || "Could not change password.");
    err.errorCode = data?.errorCode;
    throw err;
  }
  return data;
}

// Deliberately does NOT use authFetch: its auto-refresh-and-retry on any 401
// would silently exchange a stale-but-valid refresh token for a fresh access
// token (with a brand-new `iat`) and retry — which would then pass the
// backend's 15-minute reauthentication-recency check without ever prompting
// for a password, defeating the point of that check entirely.
export async function apiDeleteAccount() {
  const token = getToken();
  const res = await fetch("/api/account", {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.status === 204) return { ok: true };
  const data = await res.json().catch(() => ({}));
  if (data?.errorCode === "reauthentication_required") return { ok: false, reauthRequired: true };
  throw new Error(data?.error ?? "Could not delete account.");
}

// Decode JWT payload locally (no network call) to check expiry.
export function decodeToken(token) {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

export function isTokenExpired(token) {
  const payload = decodeToken(token);
  if (!payload?.exp) return true;
  return Date.now() / 1000 >= payload.exp;
}
